/* The two sensitivity views.
 *
 * Tornado: one bar per input, spanning the profit it produces at each end
 * of its range with everything else held at base. Sorted by span, so the
 * top bar is the input worth arguing about.
 *
 * Sweep: revenue, cost and profit as one input moves across its whole
 * range. Revenue and cost are drawn so their crossing is visible — that
 * crossing is the breakeven the tornado can only imply.
 */

import { useState } from 'react'
import { GridLines, Plot } from './Plot'
import { TipRow, TipTitle } from './hover'
import { bandValues, niceBand, polyline, xScale, yScale } from '../../lib/scale'
import { big, byFmt } from '../../lib/format'
import type { ComputeResponse } from '../../lib/api'

const TORNADO_W = 400

export function Tornado({ rows, base }: {
  rows: ComputeResponse['tornado']
  base: number
}) {
  const lo = Math.min(0, base, ...rows.map((r) => r.low))
  const hi = Math.max(0, base, ...rows.map((r) => r.high))
  const x = xScale(lo, hi, TORNADO_W)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      {rows.map((r) => (
        <TornadoRow key={r.key} row={r} base={base}>
          <div className="truncate" style={{ fontSize: 13, color: 'var(--color-neutral-800)' }}>
            {r.label}
          </div>
          <div>
            <svg viewBox={`0 0 ${TORNADO_W} 18`} preserveAspectRatio="none"
              style={{ width: '100%', height: 18, display: 'block' }}>
              <line x1="0" y1="9" x2={TORNADO_W} y2="9"
                stroke="var(--c-grid)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <rect x={x(r.low)} y="3" width={Math.max(2, x(r.high) - x(r.low))} height="12"
                fill={r.rising ? 'var(--c-bar1)' : 'var(--c-bar6)'} />
              {/* dashed = break-even, solid = where the base case sits */}
              <line x1={x(0)} y1="0" x2={x(0)} y2="18"
                stroke="var(--color-accent-900)" strokeWidth="1" strokeDasharray="2 2"
                vectorEffect="non-scaling-stroke" />
              <line x1={x(base)} y1="1" x2={x(base)} y2="17"
                stroke="var(--color-text)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
            </svg>
            <div className="num" style={{
              display: 'flex', justifyContent: 'space-between',
              fontSize: 11, color: 'var(--color-neutral-600)',
            }}>
              <span>{big(r.low)}</span><span>{big(r.high)}</span>
            </div>
          </div>
        </TornadoRow>
      ))}
    </div>
  )
}

/** One tornado bar, with the numbers the bar can only imply on hover:
 *  the swing it produces, and which way the input pushes profit. */
function TornadoRow({ row, base, children }: {
  row: ComputeResponse['tornado'][number]
  base: number
  children: React.ReactNode
}) {
  const [over, setOver] = useState(false)
  return (
    <div
      style={{
        display: 'grid', gridTemplateColumns: '170px 1fr',
        alignItems: 'center', gap: 12, position: 'relative',
      }}
      onMouseEnter={() => setOver(true)}
      onMouseLeave={() => setOver(false)}
    >
      {children}
      {over ? (
        <div style={{
          position: 'absolute', left: '50%', bottom: '100%', transform: 'translate(-50%, -6px)',
          width: 210, pointerEvents: 'none', zIndex: 5,
          background: 'var(--color-bg)', border: '1px solid var(--color-divider)',
          boxShadow: 'var(--shadow-md)', padding: '8px 10px', fontSize: 12, lineHeight: 1.45,
        }}>
          <TipTitle>{row.label}</TipTitle>
          <TipRow label="At its low" value={big(row.low)} />
          <TipRow label="At its high" value={big(row.high)} />
          <TipRow label="Swing" value={big(Math.abs(row.high - row.low))}
            color={row.rising ? 'var(--c-bar1)' : 'var(--c-bar6)'} />
          <TipRow label="Base case" value={big(base)} />
          <div style={{ marginTop: 4, color: 'var(--color-neutral-600)' }}>
            {row.rising ? 'Raising this raises profit.' : 'Raising this lowers profit.'}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function Sweep({ sweep, height = 240 }: {
  sweep: ComputeResponse['sweep']
  height?: number
}) {
  const pts = sweep.points
  const vals = pts.flatMap((p) => [p.revenue, p.cost, p.profit])
  const band = niceBand(Math.min(0, ...vals) * 1.02, Math.max(...vals) * 1.02, 4)

  const x = xScale(sweep.min, sweep.max, 1000)
  const y = yScale(band.lo, band.hi, height)
  const line = (k: 'revenue' | 'cost' | 'profit') =>
    polyline(pts.map((p) => [x(p.x), y(p[k])]))

  const xLabels = (
    <>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className="num" style={{
          textAlign: 'center', fontSize: 10, color: 'var(--color-neutral-600)',
        }}>
          {byFmt(sweep.fmt, sweep.min + ((sweep.max - sweep.min) * i) / 4)}
        </span>
      ))}
    </>
  )

  return (
    <Plot
      height={height}
      yTitle="Rupees"
      yLabels={bandValues(band.lo, band.hi, 4).map((v) => big(v))}
      xLabels={xLabels}
      xTitle={sweep.label}
      bands={pts.length}
      tooltip={(i) => {
        const p = pts[i]
        if (!p) return null
        return (
          <>
            <TipTitle>{sweep.label} {byFmt(sweep.fmt, p.x)}</TipTitle>
            <TipRow label="Revenue" value={big(p.revenue)} color="var(--c-bar4)" />
            <TipRow label="Cost" value={big(p.cost)} color="var(--c-bar2)" />
            <TipRow label="Profit" value={big(p.profit)} color="var(--color-accent-900)" />
            <TipRow label="Margin"
              value={p.revenue ? ((p.profit / p.revenue) * 100).toFixed(1) + '%' : '–'} />
          </>
        )
      }}
    >
      <GridLines ys={bandValues(band.lo, band.hi, 4).map(y)} />
      {band.lo <= 0 && band.hi >= 0 ? (
        <line x1="0" y1={y(0)} x2="1000" y2={y(0)}
          stroke="var(--color-neutral-500)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      ) : null}
      <polyline points={line('revenue')} fill="none" stroke="var(--c-bar4)"
        strokeWidth="1.8" vectorEffect="non-scaling-stroke" />
      <polyline points={line('cost')} fill="none" stroke="var(--c-bar2)"
        strokeWidth="1.8" strokeDasharray="5 3" vectorEffect="non-scaling-stroke" />
      <polyline points={line('profit')} fill="none" stroke="var(--color-accent-900)"
        strokeWidth="2" vectorEffect="non-scaling-stroke" />
      {/* where the base case sits on this axis */}
      <line x1={x(sweep.current)} y1="0" x2={x(sweep.current)} y2={height}
        stroke="var(--color-text)" strokeWidth="1" strokeDasharray="3 3"
        vectorEffect="non-scaling-stroke" />
      {sweep.zero_crossing !== null ? (
        <line x1={x(sweep.zero_crossing)} y1="0" x2={x(sweep.zero_crossing)} y2={height}
          stroke="var(--color-bad)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
      ) : null}
    </Plot>
  )
}
